<script lang="ts" setup>
import Epubjs, { Book, Contents, EpubCFI } from 'epubjs';
import type { Location as EpubLocation } from 'epubjs';
import type { ContentLocation } from '@ilteducation/activity-stream-types';
import { nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, toRefs, watch, computed } from 'vue';
import { useDisplay, useTheme } from 'vuetify';
import { storeToRefs } from 'pinia';
import { vElementSize } from '@vueuse/components';
import View from 'epubjs/types/managers/view';
import LockScreenOverlay from '../components/LockScreenOverlay.vue';
import { DYSLEXIC_FONT_TYPE_NAME } from '../util/fonts';
import Controls from '../components/Controls.vue';
import Toolbar from '../components/Toolbar.vue';
import NavigationItems from '../components/tableOfContent/NavigationItems.vue';
import Navigation from '../components/tableOfContent/Navigation.vue';
import MediaPlayer from '../components/MediaPlayer.vue';
import {
  getEpubContainerElement,
  hasWidthForSpreads,
  renderBook,
  resolveRenderingStrategy,
  viewerElementId,
} from '../util/book';
import { getViewerHeight, fixPageColors } from '../util/documentFix';
import { sendRequest, setupRequestInterceptor } from '../util/request';
import useReaderStore from '../stores/reader';
import useUiStore from '../stores/ui';
import useSettingStore from '../stores/setting';
import useMediaStore from '../stores/media';
import useRenditionStore from '../stores/rendition';
import useContent from '../composables/useContent';
import useLocale from '../composables/useLocale';
import {
  AdditionalStylesheets,
  SwitchBookPlayerModeEvent,
  TextLanguageSelectedEvent,
  Track,
  UserInformation,
} from '../types';
import { goToLocation as goToLocationEvent, pageTurned } from '../util/reading-session';
import { useContentSessionEnd } from '../composables/use-content-session-end';
import { getLastPosition } from '../util/history';
import { parse } from '../util/manifest';
import useReaderEngine from '../engine/use-reader-engine';
import useAudioTracks from '../composables/use-audio-tracks';
import useReaderNavigation from '../composables/use-reader-navigation';
import useContinuousReader from '../engine/epubjs/continuous-reader';
import usePaginatedReader, { DEFAULT_COLORS } from '../engine/epubjs/paginated-reader';
import useRenderer from '../engine/use-renderer';
import { fileRequestHandlerWithCapacitor } from '../util/localFileReader';
import { createEpubCFI, getElementFromEpubCFI, getElementFromCfi, getLocation, parseLocation } from '../util/location';
import AnnotationMenu from '../components/highlights/AnnotationMenu.vue';
import useHighlightsStore, { type HighlightWithText } from '../stores/highlights';
import AnnotationItems from '../components/annotations/AnnotationItems.vue';
import { useTouchControls } from '../stores/touch-controls';
import useNavigationTriggerStore from '../stores/navigation-trigger';
import useAudioBooksView from '../engine/epubjs/use-audio-books-view';
import { logLandmarksOnLoad, logLandmarkOnRelocate } from '../composables/use-landmarks-debug';
import NavigationChevron from '../components/Control/NavigationChevron.vue';
import debounce from 'lodash/debounce';
import useScrollLock from '../composables/use-scroll-lock';
import { useIframeMessageHandler } from '../composables/use-iframe-message-handler';
import useAudioAnnotations from '../composables/use-audio-annotations';
import Annotation from '../components/Annotation.vue';
import GoToStartBtn from '../components/Control/GoToStartBtn.vue';
import ViewerSkeletonLoader from '../components/ViewerSkeletonLoader.vue';
import { TextZoom } from '@capacitor/text-zoom';
import { Capacitor } from '@capacitor/core';
import useNavigationStore from '../stores/navigation';
import { useDocumentVisibility } from '@vueuse/core';
import ProgressThumb from '../components/Control/ProgressThumb.vue';
import PageRangeDisplayBtn from '../components/PageRangeDisplayBtn.vue';
import { EpubJsBook, EpubJsRendition } from '../types/epubjs';
import { sendBookReaderEventAnalytics } from '../composables/use-send-analytics';
import PageDisplayItem from '../components/settings/PageDisplayItem.vue';

type Props = {
  userInformation: UserInformation;
  bookId: string;
  source: string | File | ArrayBuffer;
  stylesheets: AdditionalStylesheets[];
  startPosition?: string;
  startHeading?: string;
  showTooltips: boolean;
  pageTurnAnimationEnabled?: boolean;
  pageSlideAnimationEnabled?: boolean;
  touchControlsEnabled: boolean;
  onBeforeRequest?: (url: string) => Promise<void>;
  ignoreErrors?: boolean;
};

const emit = defineEmits<{
  (e: 'click.stop'): void;
  (e: 'selectTextLanguage', event: TextLanguageSelectedEvent): void;
  (e: 'close'): void;
  (e: 'goToLogin'): void;
  (e: 'bookError', newBookError: string): void;
  (e: 'createDeeplink', createDeeplink: string): void;
  (e: 'switchBookPlayerMode', event: SwitchBookPlayerModeEvent): void;
}>();

const props = defineProps<Props>();

const { userInformation } = toRefs(props);
const { t } = useLocale();
const readerStore = useReaderStore();
const {
  book,
  bookError,
  isFrogBook,
  viewMode,
  isLocalEpubFile,
  isNative,
  isContinuous,
  isPaginated,
  navigationItems,
  annotationItems,
  serviceTheme,
  hasHighlights,
  hasBookmarks,
  hasGoToStart,
} = storeToRefs(readerStore);
const { setBookError, setBook } = readerStore;
const uiStore = useUiStore();
const {
  resetPlayerTransform,
  toggleDrawerAndControls,
  setReaderVisible,
  toggleVisibleGoToStart,
  setIsPageRangeEditing,
} = uiStore;
const {
  animationDuration,
  dyslexicFontAvailable,
  lockScreenAvailable,
  bookPlayerZoomFactor,
  playerYDragOffset,
  playerXDragOffset,
  alwaysShowControls,
  showLastPosition,
  isGoToStartVisible,
  isAnnotationBarVisible,
} = storeToRefs(uiStore);
const settingStore = useSettingStore();
const {
  isAudioNarrationDisabled,
  dyslexicFont,
  isPageSlideAnimationEnabled,
  isReflowablePageSlideAnimationEnabled,
  isTouchControlsEnabled,
  pageDisplayMode,
  isAutoFollowPlayback,
  isTextClickPlaybackEnabled,
} = storeToRefs(settingStore);
const {
  setIsPageTurnAnimationEnabled,
  setIsPageSlideAnimationEnabled,
  setIsReflowablePageSlideAnimationEnabled,
  setIsTouchControlsEnabled,
  setIsTextClickPlaybackEnabled,
  setAutoFollowPlayback,
} = settingStore;
const mediaStore = useMediaStore();
const { currentPageTracks, playing, hasMediaAvailable, currentTrack } = storeToRefs(mediaStore);
const { setMediaAvailable, setCurrentTrack, getTracksByHref, trackAnalyticsKey } = mediaStore;
const renditionStore = useRenditionStore();
const {
  isRenditionInitialized,
  slideDirection,
  isChangingPage,
  hadWidthForSpreadsWhenWeRenderedTheBook,
  currentBookmarkedTrack,
  currentTarget,
  isRightPageOnLeftSide,
  isReflowable,
  isRTL,
  renderingKey,
  rendition,
  pendingResize,
  isInitialRendering,
  isAtRenditionStart,
  isAtRenditionEnd,
  hasSpreadNone,
  isManagerNavigating,
} = storeToRefs(renditionStore);
const {
  clearCurrentTarget,
  setCurrentTarget,
  setIsChangingPage,
  setCurrentPageFiles,
  setCurrentPercentageFromPage,
  goToPreviousPage,
  setIsReflowable,
  setHasSpreadNone,
  goToPage,
  resetFontSize,
  setFontType,
  resize,
  destroyBook,
  resetState: resetRenditionState,
  setIsRenderingComplete,
  forceReRendering,
  registerBeforeReRenderCallback,
  clearAfterReRenderFlag,
} = renditionStore;
const navigationStore = useNavigationStore();
const { findInNavigation, setNavigating, setTotalSpineItems } = navigationStore;
const {
  isInitializing: isInitializingNavigation,
  enhancedToc,
  pages,
  currentPositionPct,
  getCurrentCFI,
  isScrubbing,
  navigationFlags,
  currentAudioTrackHref,
} = storeToRefs(navigationStore);
const { setCurrentTracks, playAudio, loadTracks, computeBookAudioTimings } = useAudioTracks();
const { setTotalBookAudioDuration, setBookAudioTrackTimings } = mediaStore;
const {
  goToLocation,
  goToContent,
  goToBookmark,
  goToPlayback,
  goToAnnotation,
  goToNext,
  goToPrevious,
  resetFlags,
  manualNavigation,
  currentEventCFILocation,
  suppressDrawerAndControls,
  flags,
  hasFlags: hasTransferFlags,
  isAtStart,
  isAtEnd,
  setAudioOffsetFromCfi,
} = useReaderNavigation();
const { setNavigationTrigger } = useNavigationTriggerStore();

// The chevrons are the only controls that call goToNext/goToPrevious straight from this
// view — the skip buttons, arrow keys and lock screen reach the same functions from their
// own components and set their own trigger there.
const goToPreviousFromChevron = () => {
  setNavigationTrigger('CHEVRON_LEFT');
  goToPrevious();
};
const goToNextFromChevron = () => {
  setNavigationTrigger('CHEVRON_RIGHT');
  goToNext();
};

// goToLocation is also handed to consuming apps through the rightFrame/dialog slots, so
// the trigger belongs here on the button rather than inside goToLocation itself.
const goToStart = () => {
  setNavigationTrigger('GO_TO_START');
  goToLocation(0);
};
const {
  initialise,
  tryGetSpineByIdentifier,
  getCFIFromSpineIdentifier,
  getPageSpineItem,
  manifest: manifestRef,
} = useReaderEngine();
const { addClickHandlersForAllLoadedIframes, removeClickHandlersForAllLoadedIframes, clearPlayingHighlight } =
  useAudioAnnotations();

const { mdAndDown, smAndDown, platform } = useDisplay();
const vuetifyTheme = useTheme();
const { registerListeners: registerContentListeners, handleResize, handleBookLocations } = useContent();
const readerVisibility = useDocumentVisibility();
const { init: highLightsInit, addHighlightClickListener, clearSelectedText } = useHighlightsStore();

const intervalId = ref<number | undefined>(undefined);
const isRightFrameVisible = ref(false);
const isLoading = ref(true);
const deviceTextZoom = ref(1.0);

const profileId = computed(() =>
  userInformation.value?.userIdentity.type === 'User' ? userInformation.value.userIdentity.profileId : undefined,
);

const contextMenuHighlight = ref<HighlightWithText | null>(null);
const contextMenuPosition = ref({ x: 0, y: 0 });
const showHighlightContextMenu = ref(false);
addHighlightClickListener((highlight, position) => {
  contextMenuHighlight.value = highlight;
  contextMenuPosition.value = position;
  showHighlightContextMenu.value = true;
  playing.value = false;
});

// Close context menu and pause playback when the user starts a new text selection
const { selectedText: highlightSelectedText } = storeToRefs(useHighlightsStore());
watch(highlightSelectedText, (sel) => {
  if (sel) {
    showHighlightContextMenu.value = false;
    playing.value = false;
  }
});

// Play from the annotation's anchor element — loads SMIL tracks for the correct spine item
// so it works even when the annotation is on a different page than what's currently visible.
const handlePlayFromCfi = async (cfi: string) => {
  const elementId = getElementFromCfi(cfi);
  const spineItem = getPageSpineItem(cfi);
  await playAudio(spineItem, elementId);
  sendBookReaderEventAnalytics('ANNOTATION_PLAY_FROM_CFI');
};

watch(
  () => isInitializingNavigation,
  async (isInitializing) => {
    // Only trigger when initialization FINISHES and we have a target heading
    if (!isInitializing && props.startHeading && enhancedToc.value.length > 0) {
      const results = findInNavigation(props.startHeading, true);

      if (results.length > 0) {
        const item = results[0];
        const target = item.cfi || item.href;

        setNavigationTrigger('DEEPLINK');
        goToContent({ target, isGoingForwardsInBook: true });
      }
    }
  },
  { immediate: true },
);

// Watch for changes to text click playback setting
watch(isTextClickPlaybackEnabled, (isEnabled: boolean) => {
  if (!rendition.value || !isEnabled) return;

  if (isEnabled) {
    // Add click handlers to all currently loaded iframes
    addClickHandlersForAllLoadedIframes((track) => {
      if (isAnnotationBarVisible.value) return;
      setNavigationTrigger('TEXT_CLICK');
      setCurrentTrack(track);
      mediaStore.setIsUserChangingTrack(true);
    });
  } else {
    // Remove all click handlers
    removeClickHandlersForAllLoadedIframes();
  }
});

const { endContentSession } = useContentSessionEnd();

const onPlayPause = () => {
  sendBookReaderEventAnalytics('PLAY_PAUSE_CLICKED');
  toggleVisibleGoToStart(false);
};

// Continuous books: emit one analytics event per track change. The relocated handler misses cases where currentTrack changes without a layout relocation (audio auto-advance inside a chapter, or forward/back when the next track is already buffered). Watch currentTrack directly and dedupe with a track key so we fire exactly once per real switch.
watch(currentTrack, (newTrack, oldTrack) => {
  if (!isContinuous.value) return;
  const newKey = trackAnalyticsKey(newTrack);
  if (!newKey || newKey === trackAnalyticsKey(oldTrack)) return;
  sendBookReaderEventAnalytics('POSITION_CHANGED');
});

const { registerViewerElementTouchHandlers, disableAppPinchZoom, enableAppPinchToZoom, registerBookPageTouchHandlers } =
  useTouchControls(goToNext, goToPrevious);

const { makePageScrollableAgain, makePageNotScrollable } = useScrollLock();

const gotoPage = goToPage;

const selectTextLanguage = (languageCode: string) => {
  const event: TextLanguageSelectedEvent = {
    languageCode,
  };
  const currentLocation = onEventGetCurrentLocation();

  if (currentLocation.type === 'FixedBookLocation') {
    event.position = currentLocation.page.toString();
  } else if (currentLocation.type === 'FlowableBookLocation') {
    event.position = currentLocation.position;
  }

  emit('selectTextLanguage', event);
};

const getLastmark = async (): Promise<string | undefined> => {
  if (props.startPosition) {
    return props.startPosition;
  }

  if (hasBookmarks.value || showLastPosition.value) {
    return getLastPosition(props.bookId, profileId.value);
  }
  return undefined;
};

const getCurrentTrackFromSrcElement = () =>
  currentPageTracks.value.filter(
    (currentPageTrack: Track) => currentPageTrack.text.srcFragmentId === currentBookmarkedTrack.value,
  );

const setBookmarkedTrack = () => {
  flags.value.bookmark = false;

  nextTick(() => {
    if (currentBookmarkedTrack.value) {
      const matchingTracks = getCurrentTrackFromSrcElement();
      if (matchingTracks.length > 0) {
        setCurrentTrack(matchingTracks[0]);
      }
    }
  });
};

watch(isChangingPage, (newIsChangingPage) => {
  const epubContainer = getEpubContainerElement();
  if (!epubContainer) {
    return;
  }
  if (newIsChangingPage) {
    epubContainer.classList.add('animated');
  } else {
    epubContainer.classList.remove('animated');
  }
});

watch(
  pages,
  (newPages) => {
    // If pages just loaded, sync the current position from the rendition location
    if (newPages.length > 0 && rendition.value?.location) {
      console.debug('Pages loaded, syncing position from rendition location...');
      const currentCfi = rendition.value.location.start.cfi;
      if (currentCfi) {
        setCurrentPercentageFromPage();
      }
    }
  },
  { deep: false },
);

const setDyslexicFont = (enable: boolean) => {
  setFontType(enable ? DYSLEXIC_FONT_TYPE_NAME : '');
  sendBookReaderEventAnalytics('DYSLEXIC_FONT_SET');
};

watch(dyslexicFont, () => {
  setDyslexicFont(dyslexicFont.value);
});

const onReflowable = () => {
  resetFontSize();

  if (dyslexicFontAvailable.value) {
    setDyslexicFont(dyslexicFont.value);
  }
  const height = getViewerHeight();

  resize('100%', height);

  // TODO must be done after resize? _rendition.display(lastmark);
  handleBookLocations();
};

const onEventGetCurrentLocation = (): ContentLocation => {
  const pos = isRTL.value ? Math.abs(currentPositionPct.value) : currentPositionPct.value;
  const currentCFI = getCurrentCFI.value;
  currentEventCFILocation.value = currentCFI;
  console.debug('currentEventCFILocation', currentCFI);
  return isReflowable.value
    ? { type: 'FlowableBookLocation', percentage: pos, position: currentCFI }
    : { type: 'FixedBookLocation', page: pos, position: currentCFI };
};

/**
 * Logs navigation event based on whether we have transfer flags or not
 * Used to avoid duplicate code in handlePaginatedRelocated
 */
const logNavigationEvent = () => {
  // Try to avoid logging the same location twice.
  if (currentEventCFILocation.value === getCurrentCFI.value) return;

  const eventLocation = onEventGetCurrentLocation();
  // TODO: Verify why 'left' is considered 'forward'
  const navigationDirection = slideDirection.value === 'left' ? 'forward' : 'backward';
  const navigationType = manualNavigation.value ? 'manual' : 'automatic';

  if (hasTransferFlags.value) {
    console.debug('goToLocationEvent');
    goToLocationEvent(eventLocation);
    resetFlags();
  } else {
    console.debug('pageTurnedEvent');
    pageTurned(navigationDirection, navigationType, eventLocation, isReflowable.value);
    manualNavigation.value = false;
  }
};

/**
 * Handles page relocation events for paginated mode
 * Manages state updates, navigation tracking, and event logging
 */
const handlePaginatedRelocated = async (_location: EpubLocation) => {
  clearSelectedText();
  sendBookReaderEventAnalytics('PAGE_CHANGED');
  logLandmarkOnRelocate(manifestRef.value, _location.start.index, _location.end?.index ?? _location.start.index);

  // Read before clearing the after-re-render flag: isRightPageOnLeftSide is gated by it.
  // This allows ONE correction to run, then prevents loops
  const shouldCorrectSpread = isRightPageOnLeftSide.value;
  clearAfterReRenderFlag();

  if (shouldCorrectSpread) {
    console.debug('relocated => isRightPageOnLeftSide');

    // IMPORTANT: Update position BEFORE going to previous page
    setCurrentPercentageFromPage();
    setCurrentPageFiles();
    await setCurrentTracks();

    goToPreviousPage();
    logNavigationEvent();

    // IMPORTANT: Reset a navigation flag even when returning early
    setNavigating(false);
    return;
  }

  setTimeout(
    () => {
      setIsChangingPage(false);
      if (isReflowable.value && !isAtRenditionStart.value && !isAtRenditionEnd.value) {
        handleResize();
      }
      logNavigationEvent();
    },
    animationDuration.value * 1.1 * 1000,
  );

  setCurrentPercentageFromPage();
  setCurrentPageFiles();
  await setCurrentTracks();

  // The 'content' hook fires during EPUB.js pre-render (before this page became current),
  // so tracks weren't loaded yet and click handlers were never bound at that point.
  if (isTextClickPlaybackEnabled.value) {
    addClickHandlersForAllLoadedIframes((track) => {
      if (isAnnotationBarVisible.value) return;
      setNavigationTrigger('TEXT_CLICK');
      setCurrentTrack(track);
      mediaStore.setIsUserChangingTrack(true);
    });
  }

  setNavigating(false);

  if (flags.value.bookmark) {
    setBookmarkedTrack();
  }

  // Show goToStart dialog when returning from lastmark or reaching end of book
  if (!isGoToStartVisible.value && !isAtStart.value && (flags.value.lastmark || isAtEnd.value)) {
    toggleVisibleGoToStart(true);
  }
};

/**
 * Handles the displayed event for paginated mode
 */
const handlePaginatedDisplayed = () => {
  if (isReflowable.value) {
    onReflowable();
  }
};

/**
 * This ensures that currentTrack and currentPageIndex stay aligned with the visual position.
 */
const syncMediaWithLocation = async (spineHref: string, elementId?: string) => {
  const spineItem = getPageSpineItem(spineHref);

  // RACE CONDITION FIX: Don't load tracks while the continuous manager is actively navigating
  if (!isManagerNavigating.value) {
    // Load tracks for continuous books
    await loadTracks(spineItem);
  }

  // Only set the current track if:
  // 1. Audio is not playing (safe to update position), OR
  // 2. This is an intentional navigation (currentTarget exists - from TOC or clicking a sentence)
  // This prevents automatic jumping during manual scrolling when audio is playing (background playback mode)
  if (elementId && (!playing.value || currentTarget.value)) {
    const tracks = getTracksByHref(spineHref, elementId);
    if (tracks.length > 0) {
      setCurrentTrack(tracks[0]);
    }
  }
};

/**
 * Handles location changes for continuous mode
 * Manages audio synchronization, navigation state, and auto-play logic
 */
const handleContinuousRelocated = async (location: any, _rendition: any) => {
  logLandmarkOnRelocate(manifestRef.value, location.start.index, location.end?.index ?? location.start.index);
  // Only trigger auto-play on user-initiated TOC/Bookmark navigation
  // NOT on audio auto-progression (which also sets isChangingPage/currentTarget)
  const wasUserNavigation =
    isChangingPage.value &&
    currentTarget.value &&
    isRenditionInitialized.value &&
    (navigationFlags.value.toc || navigationFlags.value.bookmark);

  // goToNextSpine (audio auto-advance in continuous mode) sets isChangingPage=true but NOT currentTarget.
  // Captured before nextTick clears these flags.
  const wasAudioAutoAdvance = isChangingPage.value && !currentTarget.value && isRenditionInitialized.value;

  if (wasUserNavigation) clearSelectedText();

  setCurrentPercentageFromPage();
  setCurrentPageFiles();

  // Determine spine and element - use currentTarget if available, otherwise use location.
  // currentTarget is not guaranteed to resolve: a shared deeplink can carry a malformed CFI, and
  // audio auto-advance can target an href that is not in this book's spine. In both cases epub.js
  // has already relocated somewhere valid, so fall back to where it actually landed rather than
  // letting the lookup throw and abort the rest of this handler.
  const targetSpine = currentTarget.value ? tryGetSpineByIdentifier(currentTarget.value) : null;
  if (currentTarget.value && !targetSpine) {
    console.warn('Could not resolve navigation target, falling back to current location:', currentTarget.value);
  }
  const spineHref = targetSpine ? targetSpine.href : location.start.href;
  const cfi = targetSpine ? await getCFIFromSpineIdentifier(currentTarget.value as string) : location.start.cfi;
  const elementId = getElementFromEpubCFI(cfi);

  await nextTick(() => {
    // We add this flag when click on TOC element. Here we remove it to unblock media player.
    setIsChangingPage(false);
    setNavigating(false);
    // Clear currentTarget after navigation to prevent it from affecting subsequent manual scrolls
    clearCurrentTarget();
  });

  // Auto-play logic:
  // - YES: When user navigates via TOC/bookmarks AND playback was already running
  // - NO: On initial book load (lastmark)
  // - NO: During audio auto-progression
  // - NO: During manual scrolling with audio playing
  // - NO: When navigating while paused — user must explicitly start playback
  if (wasUserNavigation && elementId && playing.value) {
    const spineItem = getPageSpineItem(spineHref);
    playAudio(spineItem, elementId);
    return;
  }

  // For all other location changes (scrolling, lastmark, etc.):
  // Skip syncing during scrubbing to prevent slider jump
  if (isScrubbing.value) {
    return;
  }

  // Skip syncing during initial book load/rendering to prevent auto-play on lastmark
  if (!isRenditionInitialized.value) {
    return;
  }

  // Check if audio track is in active zone BEFORE early returns (continuous mode specific)
  // This needs to run even during manual scrolling to detect when user scrolls outside zone
  if (playing.value && (_rendition as EpubJsRendition)?.manager?.isViewInActiveZone && currentAudioTrackHref.value) {
    console.debug('Checking active zone for current audio track...');
    try {
      const currentAudioSpineItem = getPageSpineItem(currentAudioTrackHref.value);
      if (currentAudioSpineItem) {
        const manager = (_rendition as EpubJsRendition)?.manager;
        const isInZone = manager?.isViewInActiveZone?.(currentAudioSpineItem.index);

        if (!isInZone && isAutoFollowPlayback.value) {
          console.debug('Audio track outside active zone - disabling auto-follow');
          setAutoFollowPlayback(false);
          toggleVisibleGoToStart(true);
        } else if (isInZone && isGoToStartVisible.value) {
          console.debug('Audio track back in active zone - hiding go to start button');
          toggleVisibleGoToStart(false);
          // Note: Auto-follow remains OFF until user explicitly re-enables by clicking sentence or navigating
        }
      }
    } catch (error) {
      console.error('Error checking active zone:', error);
    }
  }

  // Skip syncing during manual scrolling when audio is playing (background playback mode)
  // This prevents automatic jumping while allowing sentence clicks to work
  // (Sentence clicks call playAudio directly, so they don't rely on this sync)
  // EXCEPTION: If currentTarget is set while playing, audio progressed to a new location
  // and we SHOULD sync to follow it
  if (playing.value && !currentTarget.value && !wasAudioAutoAdvance) {
    //TODO: Remove !currentTarget check, as currentTarget gets cleared out above?
    return;
  }

  // Safe to sync: audio is paused/stopped and book has finished initial rendering
  await syncMediaWithLocation(spineHref, elementId);

  // RACE CONDITION FIX: Don't load tracks while the continuous manager is actively navigating
  // This prevents SMIL loading from triggering while the manager is building its buffer
  if (!isManagerNavigating.value) {
    await setCurrentTracks();
  }

  // Show goToStart dialog when returning from lastmark (continuous mode)
  if (!isGoToStartVisible.value && !isAtStart.value && flags.value.lastmark) {
    toggleVisibleGoToStart(true);
  }
  // TODO send activity-stream event? Or, send based on audio-tracks instead?
  // TODO scroll along with audio?
};

const { setup: setupContinuousReader } = useContinuousReader();
const { setup: setupPaginatedReader, getThemeColors } = usePaginatedReader();

setIsTouchControlsEnabled(props.touchControlsEnabled && platform.value.touch);

onMounted(async () => {
  disableAppPinchZoom();
  resetPlayerTransform();

  // Add event listener on the window to listen for 'message' events (sent via postMessage from the iframe), used to e.g. close overlays.
  window.addEventListener('message', handleIframeMessage);

  //Disable text click playback on touch device (except for continuous books)
  const enableTextClickPlayback = isContinuous.value || !platform.value.touch;
  setIsTextClickPlaybackEnabled(enableTextClickPlayback);

  //Don't let dynamic font scaling affect book text sizes on Android, due to it causing text to overlap on some books.
  if (isTouchControlsEnabled.value && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    const currentTextZoom = await TextZoom.get();
    if (currentTextZoom.value > 1.0) {
      deviceTextZoom.value = currentTextZoom.value;
      await TextZoom.set({ value: 1.0 });
    }
  }
});

onUnmounted(async () => {
  // It's placed here to let session events to be processed in child components onBeforeUnmount
  endContentSession();

  enableAppPinchToZoom();

  destroyBook();
  resetRenditionState();

  window.removeEventListener('message', handleIframeMessage);

  if (
    isTouchControlsEnabled.value &&
    platform.value.android &&
    Capacitor.isNativePlatform() &&
    deviceTextZoom.value > 1.0
  ) {
    await TextZoom.set({ value: deviceTextZoom.value });
  }
});

onBeforeUnmount(async () => {
  clearInterval(intervalId.value);
  intervalId.value = undefined;

  makePageScrollableAgain();
});

const request = async (url: string, type: string) => {
  // Used to check if CDN token is still valid
  if (props.onBeforeRequest) {
    await props.onBeforeRequest(url);
  }

  return sendRequest(url, type);
};

const loadEpubJSBook = async (): Promise<Book> => {
  // @ts-ignore missing types
  const epubJSBook = new Epubjs(props.source, {
    requestMethod: isLocalEpubFile.value ? fileRequestHandlerWithCapacitor : request,
  });

  epubJSBook.on('openFailed', (e: any) => {
    console.error('openFailed');

    console.error(e);
    setBookError('error_fault_in_book_message');
  });

  await epubJSBook.ready;

  return epubJSBook;
};

watch(renderingKey, () => {
  renderBookAndDoAllTheStuffWeDoWhenWeInitializeTheRendering();
});

// Watch for page display mode changes and re-render the book
watch(pageDisplayMode, (newMode, oldMode) => {
  // Only re-render if the book is already loaded and the mode actually changed
  if (book.value && oldMode !== undefined && newMode !== oldMode) {
    // Clear stale annotation and click-handler state before the rendition is destroyed,
    // so the new rendition's content hooks start from a clean slate.
    clearPlayingHighlight();
    removeClickHandlersForAllLoadedIframes();
    forceReRendering();
  }
});

const renderBookAndDoAllTheStuffWeDoWhenWeInitializeTheRendering = async () => {
  isLoading.value = true;
  isInitialRendering.value = true; // Disable all resizes now
  toggleVisibleGoToStart(false);

  // Clear any existing scroll locks before rendering book
  makePageScrollableAgain();

  // Capture the current rendering key to detect if this render becomes stale
  const currentRenderingKey = renderingKey.value;

  Promise.all([loadEpubJSBook(), getLastmark()]).then(async ([epubJSBook, lastmark]) => {
    // Check if this render is still current (hasn't been superseded by a new re-render)
    if (currentRenderingKey !== renderingKey.value) {
      console.debug('Abandoning stale render, new render has started');
      return;
    }

    const manifest = parse(epubJSBook);

    initialise(manifest, epubJSBook, isLocalEpubFile.value);
    // After initialise(): it resets the navigation store, flags included.
    flags.value.lastmark = !!lastmark;

    setBook(epubJSBook);
    window.document.title = `${manifest.title} - ${manifest.creator}`;

    const hasMediaAvailable: boolean = !isAudioNarrationDisabled.value && Object.keys(manifest.smilItems).length > 0;

    setMediaAvailable(hasMediaAvailable);

    // Continuous books load SMIL lazily as the user scrolls, so playlist.totalDuration stays at 0 and individual tracks only know their offset within their chapter audio file. Pre-resolve every chapter's SMIL once so analytics can report the book's full narrated duration plus a monotonic per-track cumulative position.
    if (isContinuous.value && hasMediaAvailable) {
      computeBookAudioTimings(manifest.spineItems).then(({ totalDuration, trackTimings }) => {
        setTotalBookAudioDuration(totalDuration);
        setBookAudioTrackTimings(trackTimings);
      });
    }

    /**
     * We need to know if the book is reflowable before we render it, so we know which rendering options to use.
     * Before we used the rendition.settings.layout, but that is updated only after we render the book (rendition object is created when rendering the book).
     *
     * This below is the logic used in epubjs -> rendition.js to determine if layout is paginated.
     *
     * We should not apply "reflowable" fixes for continuous view. So there is exception for this added.
     */
    const isPrePaginated =
      // @ts-ignore These properties exist actually
      epubJSBook.package.metadata.layout === 'pre-paginated' || epubJSBook.displayOptions.fixedLayout === 'true';
    setIsReflowable(!isPrePaginated && !isContinuous.value);

    // Use manifest data instead of direct EPUBjs access
    const hasSpreadNone = manifest.spread === 'none' || isFrogBook.value;
    setHasSpreadNone(hasSpreadNone);

    const totalSpine = (epubJSBook as EpubJsBook).spine?.spineItems?.length || 0;
    setTotalSpineItems(totalSpine);

    // Check if user prefers single-page mode
    // Works for both pre-paginated AND reflowable books (but not if book already has spread=none)
    const userPrefersSinglePage = !hasSpreadNone && pageDisplayMode.value === 'single';

    /*
    Resolve which manager/view pair the book will actually be rendered with, and derive the settings flags from
    that same answer instead of from a second, hand-maintained copy of the condition.

    These flags are not cosmetic: touch-controls uses isPageTurnAnimationEnabled to decide whether the rendered view
    is a FlippingIframeView and calls methods that only exist on that class. Anything that changes the strategy
    (spread=none, single-page preference, screen too narrow) has to move both flags with it.
     */
    const renderingStrategy = resolveRenderingStrategy({
      pageTurnAnimationRequested: !!props.pageTurnAnimationEnabled,
      pageSlideAnimationRequested: !!props.pageSlideAnimationEnabled,
      isPrePaginated,
      isContinuous: isContinuous.value,
      // Use the store's flag which is properly updated before re-rendering
      hasWidthForSpread: hadWidthForSpreadsWhenWeRenderedTheBook.value,
      hasSpreadNone,
      userPrefersSinglePage,
    });

    setIsPageTurnAnimationEnabled(renderingStrategy === 'page-turn');
    setIsPageSlideAnimationEnabled(renderingStrategy === 'page-slide');
    setIsReflowablePageSlideAnimationEnabled(renderingStrategy === 'reflowable-page-slide');

    if (renderingStrategy === 'page-turn') {
      makePageNotScrollable();
    }

    renderBook(
      epubJSBook,
      isContinuous.value,
      renderingStrategy,
      platform.value.touch || false,
      isFrogBook.value,
      platform.value,
      hasSpreadNone, // Pass spread=none info to enable SliderManager
      userPrefersSinglePage, // User preference from settings (works for both pre-paginated and reflowable)
      hadWidthForSpreadsWhenWeRenderedTheBook.value, // Pass store's pre-computed value
    );

    if (hasHighlights.value && props.bookId) {
      highLightsInit(props.bookId, epubJSBook.rendition, profileId.value);
      console.log('Initialized highlights with bookId:', props.bookId, 'profileId:', profileId.value);

      // TextActionMenu is now handled by markClicked in reader engines (Phase 3.7)
      // Menu only shows on actual audio text clicks, not on empty space
    }

    // Clear possible target value stored in store.
    clearCurrentTarget();

    logLandmarksOnLoad(manifest);

    if (isContinuous.value) {
      setupContinuousReader(epubJSBook.rendition, props.stylesheets, {
        onRelocated: handleContinuousRelocated,
      });
    } else {
      setupPaginatedReader(epubJSBook.rendition, props.stylesheets, {
        registerViewerElementTouchHandlers,
        registerBookPageTouchHandlers,
        onRelocated: handlePaginatedRelocated,
        onDisplayed: handlePaginatedDisplayed,
      });

      if (hasHighlights.value && props.bookId) {
        highLightsInit(props.bookId, epubJSBook.rendition, profileId.value);
      }
    }

    const { epubCFI } = parseLocation(lastmark as string);

    if (epubCFI && lastmark) {
      setAudioOffsetFromCfi(lastmark); // set audio offset in case it exists
      setCurrentTarget(epubCFI); // Just use the original CFI directly!
    }

    try {
      // Case 1: No saved position. Go straight to the beginning.
      if (!epubCFI) {
        await epubJSBook.rendition.display(0);
        return;
      }

      // Set here rather than in getLastmark(): fetching the position happens in parallel
      // with loading the book, so the gap to the relocation would otherwise be long enough
      // for the trigger to expire on a slow load.
      setNavigationTrigger(props.startPosition ? 'DEEPLINK' : 'RESTORE_POSITION');

      // Case 2: We have a CFI. Let's try to load it.
      try {
        await epubJSBook.rendition.display(epubCFI);
        return;
      } catch (cfiError) {
        console.warn('Failed to display precise CFI. Attempting chapter fallback...', epubCFI, cfiError);

        // The CFI could not be displayed, so it is stale or malformed and must not stay the
        // navigation target — otherwise the first 'relocated' event tries to resolve it against
        // the spine, long after we have fallen back to a different position.
        clearCurrentTarget();

        // Case 3: CFI is stale/invalid. Try to fall back to the start of the chapter (spine).
        try {
          const pos = new EpubCFI(epubCFI).spinePos;
          if (typeof pos === 'number') {
            await epubJSBook.rendition.display(pos);
            return;
          }
        } catch (spineError) {
          console.warn('Chapter fallback failed.', spineError);
        }
      }

      // Case 4: CFI and Spine fallback both failed. Go to the beginning.
      await epubJSBook.rendition.display(0);
    } catch (criticalError) {
      console.error('CRITICAL: Failed to render the book entirely.', criticalError);
      throw criticalError;
    } finally {
      isInitialRendering.value = false;
      isLoading.value = false;
      await nextTick(() => {
        setIsRenderingComplete();
        if (pendingResize.value) {
          console.debug('Rendering complete, executing queued resize');
          handleResize({
            width: pendingResize.value.width as number,
            height: pendingResize.value.height as number,
          });
        }
      });
    }
    // TODO: i think we could move this elsewhere, as it doesn't need to be inside the "then" of loading the book?
    const { addTitleToIframe } = useRenderer();
    epubJSBook.rendition.hooks.content.register((contents: Contents) =>
      addTitleToIframe(contents, epubJSBook.navigation?.toc ?? [], manifest.title),
    );
  });
};

// Reset these to avoid stale values during re-rendering that causes incorrect manager/view functions being called
// e.g. in touch-controls.ts.
const resetStateBeforeReRender = () => {
  setIsPageTurnAnimationEnabled(false);
  setIsPageSlideAnimationEnabled(false);
  setIsReflowablePageSlideAnimationEnabled(false);
};

registerBeforeReRenderCallback(resetStateBeforeReRender);

renderBookAndDoAllTheStuffWeDoWhenWeInitializeTheRendering();

setupRequestInterceptor(setBookError);
registerContentListeners();
// init done

const vFocus = {
  mounted: (el: { focus: () => any }) => el.focus(),
};

const onReSizeViewer = debounce(() => {
  clearSelectedText();
  handleResize();
}, 500);

const onReSizeRightFrame = (size: { width: number; height: number }) => {
  if (isContinuous.value) {
    /**
     * In case if iPhone 13 and some specific book 'rendition.resize();' inside "handleResize" function led
     * to blank screen in book player.
     * Actually the book is rendered, but iframes got visibility:hidden.
     * My assumption is this 'rendition.resize();' triggered while book is being rendered caused this.
     * So as a workaround we are disable resize of this slot for continuous books.
     * Also, height and width in case of continuous mode are equal 100%, but handleResize sets values in pixels.
     */
    return;
  }
  isRightFrameVisible.value = size.width > 0;
  onReSizeViewer();
};

watch(bookError, (newBookError) => {
  if (newBookError) {
    emit('bookError', newBookError);
  }
});

const createDeeplink = async () => {
  const location = await getLocation();
  sendBookReaderEventAnalytics('DEEP_LINK_CREATED');
  emit('createDeeplink', createEpubCFI(location));
};

watch(
  () => serviceTheme.value,
  (value) => {
    const colors = getThemeColors(value);
    const allViews: View[] = rendition.value?.manager.views.all() ?? [];

    uiStore.setThemeMode(vuetifyTheme.global.current.value.dark ? 'dark' : 'light');
    if (viewMode.value === 'continuous') {
      const { colouriseByPage } = useAudioBooksView();
      const { applyThemeAndBackground } = useRenderer();
      for (const view of allViews) {
        const contents = (view as any)?.contents; // TODO: fix type
        if (contents) {
          applyThemeAndBackground(contents, 'continuous');
          colouriseByPage(contents, false);
        }
      }
    } else {
      for (const view of allViews) {
        fixPageColors(
          view,
          viewMode.value,
          isReflowable.value,
          isFrogBook.value,
          colors.text,
          colors.pageBackground,
          colors.viewerBackground,
        );
      }
    }
  },
);

watch(readerVisibility, () => {
  setReaderVisible(readerVisibility.value === 'visible');
});

const shouldShowGoToPlayback = computed(() => {
  return isContinuous.value && !isAutoFollowPlayback.value && playing.value && hasMediaAvailable.value;
});

const viewerSurfaceBackgroundColor = computed<string>(() => {
  /**
   *  FIXME, Staffan 🙏
   */
  const theme = vuetifyTheme.themes.value[serviceTheme.value] || vuetifyTheme.current.value;

  return theme?.colors.surface ?? DEFAULT_COLORS.background;
});

// Setup iframe message handler for keyboard shortcuts and clicks
const { handleIframeMessage } = useIframeMessageHandler({
  onPlayPause,
  goToNext,
  goToPrevious,
});

const onClick = (event: MouseEvent | TouchEvent) => {
  event.preventDefault();
  setIsPageRangeEditing(false);
  if (suppressDrawerAndControls.value) {
    return;
  }
  toggleDrawerAndControls(true, true);
};
</script>

<template>
  <v-overlay
    :model-value="isLoading"
    contained
    scrim="surface"
    height="100%"
    width="100%"
    class="align-center justify-center"
  >
    <ViewerSkeletonLoader :is-spread="!smAndDown && !isContinuous" :is-continuous="isContinuous" />
  </v-overlay>
  <div id="main" data-testid="bp-reader-view">
    <v-snackbar
      color="error"
      :model-value="!ignoreErrors && !!bookError"
      location="top"
      multi-line
      timeout="-1"
      :class="{ 'mt-5': isNative }"
    >
      <span>{{ t(bookError!) }}</span>
      <template #actions>
        <v-btn v-focus tabindex="0" data-testid="bp-error-login-btn" @click.stop="emit('goToLogin')">
          <span>{{ t('button_log_in_again') }}</span>
        </v-btn>
      </template>
    </v-snackbar>
    <div class="d-flex flex-column-reverse" v-if="(!bookError || ignoreErrors) && book">
      <LockScreenOverlay v-if="lockScreenAvailable" :show-tooltips="showTooltips" />
      <Controls
        v-if="isRenditionInitialized"
        :show-tooltips="showTooltips"
        :profile-id="profileId"
        :isLoading="isLoading"
        @play-pause="onPlayPause"
        @go-to-content="goToContent"
        @go-to-next="goToNext"
        @go-to-previous="goToPrevious"
        @select-text-language="selectTextLanguage"
        @switch-book-player-mode="emit('switchBookPlayerMode', $event)"
      >
        <PageDisplayItem
          v-if="!isContinuous && !hasSpreadNone && hasWidthForSpreads(platform.touch)"
          v-model="pageDisplayMode"
        />
        <template #controls-actions>
          <slot name="controls-actions" :create-deeplink="createDeeplink" />
        </template>
        <template #customSettings>
          <slot name="customSettings" />
        </template>
      </Controls>
      <GoToStartBtn
        v-if="hasGoToStart || shouldShowGoToPlayback"
        :is-at-end="isAtEnd"
        :is-from-lastmark="flags.lastmark"
        :show-go-to-playback="shouldShowGoToPlayback"
        @go-to-start="goToStart"
        @go-to-playback="goToPlayback"
        @clear-lastmark="flags.lastmark = false"
      />
      <ProgressThumb>
        <PageRangeDisplayBtn @go-to-content="goToContent" />
      </ProgressThumb>
      <AnnotationMenu
        :profile-id="profileId"
        :edit-highlight="contextMenuHighlight"
        :edit-position="contextMenuPosition"
        :show-edit="showHighlightContextMenu"
        @update:show-edit="showHighlightContextMenu = $event"
        @play-from-cfi="handlePlayFromCfi"
      />
      <Toolbar
        @close="() => emit('close')"
        @switch-book-player-mode="emit('switchBookPlayerMode', $event)"
        :show-tooltips="showTooltips"
        :profile-id="profileId"
      >
        <template #toolbar-actions>
          <slot name="toolbar-actions" :create-deeplink="createDeeplink" />
        </template>
        <template #close-btn="{ closePlayer, displayNone }">
          <slot name="close-btn" :close-player="closePlayer" :display-none="displayNone" />
        </template>
      </Toolbar>
      <div class="pa-0 fill-height" tabindex="0">
        <v-sheet class="d-flex flex justify-space-between">
          <v-sheet
            class="book-frame"
            data-testid="bp-book-frame"
            :class="{
              'with-right-frame': isRightFrameVisible && !smAndDown,
              'native-app': isNative,
              'web-app': !isNative,
              'book-frame-continuous': isContinuous,
            }"
            :color="viewerSurfaceBackgroundColor"
            @click="onClick"
          >
            <div class="d-flex justify-center w-100">
              <v-btn
                v-if="!alwaysShowControls"
                class="d-sr-only-focusable text-caption text-none"
                variant="flat"
                density="compact"
                :aria-label="t('label_open_reader_controls')"
                @click="onClick"
              >
                {{ t('label_open_reader_controls') }}
              </v-btn>
            </div>
            <NavigationChevron v-if="isPaginated" direction="left" @navigate="goToPreviousFromChevron" />
            <NavigationChevron v-if="isPaginated" direction="right" @navigate="goToNextFromChevron" />
            <div
              :id="viewerElementId"
              data-testid="bp-viewer"
              tabindex="-1"
              v-element-size="onReSizeViewer"
              :class="{
                spreads: !isReflowable,
                'reflowable-spreads': isReflowable && !smAndDown && !isContinuous,
                'reflowable-mobile-spreads': isReflowable && mdAndDown && !isContinuous,
                'limit-height': (isReflowable || alwaysShowControls) && !smAndDown && !isContinuous,
                'limit-height-mobile': (isReflowable || alwaysShowControls) && mdAndDown && !isContinuous,
                'slide-to-left': slideDirection === 'left',
                'slide-to-right': slideDirection === 'right',
                'view-continuous': isContinuous && !smAndDown,
                'view-continuous-mobile': isContinuous && smAndDown,
                'view-paginated': isPaginated,
                'has-slide-animation': isPageSlideAnimationEnabled,
                'has-reflowable-slide-animation': isReflowablePageSlideAnimationEnabled,
              }"
              :style="{
                transform: `translate3d(${playerXDragOffset}px, ${playerYDragOffset}px, 0) scale3d(${bookPlayerZoomFactor}, ${bookPlayerZoomFactor}, 1)`,
                transformOrigin: `center center`,
              }"
            />
          </v-sheet>
          <v-sheet v-element-size="onReSizeRightFrame" class="d-flex flex-grow-0 right-frame">
            <slot name="rightFrame" :change-page="goToLocation" :goto-page="gotoPage"></slot>
          </v-sheet>
        </v-sheet>
      </div>
      <Navigation v-slot="slotProps" :navigation-items="navigationItems">
        <NavigationItems
          :tab="slotProps.tab"
          :search="slotProps.search"
          :navigation-items="slotProps.navigationItems"
          :book-id="bookId"
          @go-to-content="goToContent"
          @go-to-bookmark="goToBookmark"
          @go-to-annotation="goToAnnotation"
          @reset-search="slotProps.resetSearch"
        />
      </Navigation>
      <Annotation v-slot="slotProps" :annotation-items="annotationItems">
        <AnnotationItems
          :tab="slotProps.tab"
          :search="slotProps.search"
          :annotation-items="slotProps.annotationItems"
          :book-id="bookId"
          :profile-id="profileId"
          @go-to-bookmark="goToBookmark"
          @go-to-annotation="goToAnnotation"
          @reset-search="slotProps.resetSearch"
        />
      </Annotation>
      <MediaPlayer />
      <slot name="dialog" :change-page="goToLocation" :goto-page="gotoPage"></slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

$slide-directions: left, right;
$bar-top-height: 64px;
$bar-bottom-height: 56px;
$bar-bottom-mobile-height: 128px;
$bar-vertical-margin: 0px;
$navigation-chevron-width: 64px;
$navigation-chevron-mobile-width: 30px;
$mobile-bar-vertical-margin: 0px;

#main {
  background-color: rgb(var(--v-theme-surface));
  display: flex;
  width: 100%;
  height: 100%;

  .book-frame {
    position: relative;
    min-width: 100vw;

    &.book-frame-continuous {
      width: 100vw; // fixes resize to smaller sizes
    }

    &.with-right-frame {
      min-width: 70vw;
      @media #{map.get(v.$display-breakpoints, 'lg-and-down')} {
        min-width: 60vw;
      }
      @media #{map.get(v.$display-breakpoints, 'sm-and-down')} {
        min-width: 50vw;
      }
    }
  }

  .right-frame {
    z-index: 2;
    background-color: rgb(var(--v-theme-surface-bright));
    max-height: calc(100vh);
    max-height: calc(100dvh); //for mobile browsers
    min-height: calc(100vh);
    min-height: calc(100dvh); //for mobile browsers
    @media #{map.get(v.$display-breakpoints, 'md-and-down')} {
      max-height: calc(100vh);
      max-height: calc(100dvh); //for mobile browsers
      min-height: calc(100vh);
      min-height: calc(100dvh); //for mobile browsers
    }
  }
}

#viewer {
  background: transparent !important;
  height: 100dvh; //for mobile browsers

  &.spreads:not(.view-continuous) {
    position: absolute;
    height: 100vh;
    height: 100dvh; //for mobile browsers
    width: 100%;
  }

  &.reflowable-spreads,
  &.reflowable-mobile-spreads {
    position: absolute;
  }

  &.reflowable-spreads {
    width: calc(100% - #{2 * $navigation-chevron-width}) !important;
    left: $navigation-chevron-width !important;
  }

  &.reflowable-mobile-spreads {
    width: calc(100% - #{2 * $navigation-chevron-mobile-width}) !important;
    left: $navigation-chevron-mobile-width !important;
  }

  &.limit-height {
    top: $bar-top-height;
    height: calc(100vh - #{$bar-top-height + $bar-bottom-height});
    height: calc(100dvh - #{$bar-top-height + $bar-bottom-height}); //for mobile browsers
    max-height: calc(100vh - #{$bar-top-height + $bar-bottom-height});
    max-height: calc(100dvh - #{$bar-top-height + $bar-bottom-height}); //for mobile browsers
  }

  &.limit-height-mobile {
    top: $bar-top-height;
    height: calc(100vh - #{$bar-top-height + $bar-bottom-mobile-height});
    height: calc(100dvh - #{$bar-top-height + $bar-bottom-mobile-height}); //for mobile browsers
    max-height: calc(100vh - #{$bar-top-height + $bar-bottom-mobile-height});
    max-height: calc(100dvh - #{$bar-top-height + $bar-bottom-mobile-height}); //for mobile browsers
  }

  &.view-continuous {
    height: calc(100vh - #{$bar-bottom-height});
    height: calc(100dvh - #{$bar-bottom-height}); //for mobile browsers
    max-height: calc(100vh - #{$bar-bottom-height});
    max-height: calc(100dvh - #{$bar-bottom-height}); //for mobile browsers
    padding-top: env(safe-area-inset-top); // to prevent title being hidden under phones notch
  }
  &.view-continuous-mobile {
    height: calc(100vh - #{$bar-bottom-mobile-height});
    height: calc(100dvh - #{$bar-bottom-mobile-height}); //for mobile browsers
    max-height: calc(100vh - #{$bar-bottom-mobile-height});
    max-height: calc(100dvh - #{$bar-bottom-mobile-height}); //for mobile browsers
    padding-top: env(safe-area-inset-top); // to prevent title being hidden under phones notch
  }

  &.view-continuous .epub-view:last-child {
    margin-bottom: 0;
    @media #{map.get(v.$display-breakpoints, 'sm-and-up')} {
      margin-bottom: $bar-bottom-height;
    }
  }

  // Slide animation for reflowable books only. With has-reflowable-slide-animation, ReflowableSliderManager slides instead.
  &.reflowable-spreads:not(.has-reflowable-slide-animation) {
    @each $direction in $slide-directions {
      &.slide-to-#{$direction} .epub-container.animated {
        #{$direction}: 100%;
        animation-name: slide-to-#{$direction};
        animation-fill-mode: forwards;
      }
    }
  }

  // Enhanced slide animation with translate3d for better performance (reflowable books only)
  &.reflowable-spreads.view-paginated.has-slide-animation {
    .epub-container {
      transition: transform 0.35s ease-out;
      will-change: transform;
    }

    &.slide-to-left .epub-container.animated {
      transform: translate3d(-100%, 0, 0);
      animation: none;
    }

    &.slide-to-right .epub-container.animated {
      transform: translate3d(100%, 0, 0);
      animation: none;
    }

    .epub-container.animated {
      transform: translate3d(0, 0, 0);
    }
  }
}

@each $direction in $slide-directions {
  @keyframes slide-to-#{$direction} {
    100% {
      #{$direction}: 0;
    }
  }
}

#viewer {
  &.spreads {
    height: 100vh;
    height: 100dvh; //for mobile browsers
  }
}

.media-device-android-tablet {
  #viewer {
    &.spreads {
      height: 100vh;
      height: 100dvh; //for mobile browsers
      width: calc(100vw - #{2 * $navigation-chevron-width});
      left: $navigation-chevron-width;
    }
  }
}
</style>
